import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GeneralSelect, SearchBox, ToggleButton } from 'sharedComponents';
import { LoadingIndicator } from 'sharedComponents/PowertrainLoadingIndicator';
import { getAssetIcon } from 'helpers/icons';
import HierarchyViewContainer from '../../ConditionIndicesOverviewV2/HierarchyView/HierarchyViewContainer';
import { iconCondition } from '../../ConditionIndicesOverviewV2/helpers';
import { conditionIndicesViewOptions, toggleButtonOptions } from '../../ConditionIndicesOverviewV2/constants';
import { allAssetTypes } from 'helpers/constants';
import { browserCategories } from '../constants';
import './style.scss';

const AssetBrowserPanelComponent = ({
    selectedAsset,
    selectedOrganization,
    conditionIndicesView,
    onSelectAsset,
    searchText,
    assetTypeFilter,
    assetTypeOptions,
    onSearchChange,
    onAssetTypeChange,
    leftPanelSize,
    selectedCategory,
    onCategoryChange,
    powertrainOptions,
    selectedPowertrainId,
    onPowertrainChange,
    powertrainAssets,
    isPowertrainLoading,
    selectedAssetIds,
    selectedAssetSignalCounts
}) => {
    const { t: translate } = useTranslation();
    const isListView = conditionIndicesView === conditionIndicesViewOptions.LIST;
    const isPowertrainCategory = selectedCategory === browserCategories.POWERTRAINS;
    const normalizedAssetTypeFilter = assetTypeFilter || allAssetTypes;
    const categoryToggleOptions = useMemo(() => [
        {
            label: translate('ABB.Powertrain.Frontend.powertrainsLabel'),
            value: browserCategories.POWERTRAINS
        },
        {
            label: translate('ABB.Powertrain.Frontend.assetsLabel'),
            value: browserCategories.ASSETS
        }
    ], [translate]);

    const assetTypeSelectOptions = useMemo(() => {
        return (assetTypeOptions || []).map((assetType) => ({
            id: assetType,
            assetType,
            title: assetType === allAssetTypes
                ? translate('ABB.Powertrain.Frontend.allassettypesLabel') || 'All asset types'
                : translate(`ABB.Powertrain.Frontend.${assetType?.toLowerCase()}Label`) || assetType
        }));
    }, [assetTypeOptions, translate]);

    const selectedAssetTypeOption = useMemo(() => {
        return assetTypeSelectOptions.find((option) => option.assetType === normalizedAssetTypeFilter) || null;
    }, [assetTypeSelectOptions, normalizedAssetTypeFilter]);

    const powertrainSelectOptions = useMemo(() => {
        return (powertrainOptions || []).map((powertrain) => ({
            id: powertrain.id,
            title: powertrain.label
        }));
    }, [powertrainOptions]);

    const selectedPowertrainOption = useMemo(() => {
        if (selectedPowertrainId === null || selectedPowertrainId === undefined) {
            return null;
        }

        return powertrainSelectOptions.find((option) => option.id?.toString() === selectedPowertrainId?.toString()) || null;
    }, [powertrainSelectOptions, selectedPowertrainId]);

    const renderPowertrainAssets = () => {
        if (isPowertrainLoading) {
            return (
                <div className='asset-browser-list-empty'>
                    <LoadingIndicator />
                </div>
            );
        }

        if (!powertrainAssets?.length) {
            return <div className='asset-browser-list-empty'>{translate('ABB.Powertrain.Frontend.conditionIndicesOverviewNoItems')}</div>;
        }

        return <div className='asset-browser-powertrain-assets-list'>
            {powertrainAssets.map((asset, index) => {
                const resolvedId = asset?.assetId ?? asset?.id;
                const isSelected = resolvedId?.toString() === (selectedAsset?.assetId ?? selectedAsset?.id)?.toString();
                const assetName = asset?.assetName || asset?.name || asset?.componentName;
                const assetType = asset?.assetType || asset?.componentTypeName || asset?.assetTypeName;
                const condition = asset?.condition || asset?.conditionIndexCondition || asset?.overallCondition;
                const hasSelectionDot = !!resolvedId && selectedAssetIds?.includes(resolvedId.toString());
                const selectionCount = resolvedId ? selectedAssetSignalCounts?.[resolvedId.toString()] || 0 : 0;
                return <button
                    key={resolvedId || index}
                    type='button'
                    className={`asset-browser-powertrain-asset-row ${isSelected ? 'selected' : ''}`}
                    onClick={() => onSelectAsset?.(asset)}
                >
                    <span className={`asset-browser-powertrain-asset-condition ${iconCondition(condition)}`}></span>
                    <span className='asset-browser-powertrain-asset-icon'>
                        {getAssetIcon(assetType, { width: 16, height: 16 })}
                    </span>
                    <span className='asset-browser-powertrain-asset-name'>
                        {assetName || '-'}
                    </span>
                    {hasSelectionDot &&
                        <span className='asset-browser-powertrain-asset-selection-dot' aria-hidden='true'>
                            {selectionCount}
                        </span>
                    }
                </button>;
            })}
        </div>;
    };

    return (
        <div className='asset-browser-panel'>
            <div className='asset-browser-top-filters'>
                {isListView &&
                    <div className='asset-browser-category-toggle'>
                        <ToggleButton
                            options={categoryToggleOptions}
                            selectedValue={selectedCategory}
                            onSelected={(category) => onCategoryChange?.(category)}
                        />
                    </div>
                }

                <SearchBox
                    className='asset-browser-search'
                    placeholder={translate('ABB.Powertrain.Frontend.nameOrSerialNoLabel')}
                    onChange={onSearchChange}
                    value={searchText || ''}
                />

                {isListView && isPowertrainCategory &&
                    <GeneralSelect
                        className='asset-browser-powertrain-filter'
                        inputId='asset-browser-powertrain-filter'
                        options={powertrainSelectOptions}
                        getOptionLabel={(option) => option.title}
                        getOptionValue={(option) => option.id}
                        value={selectedPowertrainOption}
                        placeholder={translate('ABB.Powertrain.Frontend.powertrainsLabel')}
                        onChange={(value) => onPowertrainChange?.(value?.id?.toString() || '')}
                        isSearchable={false}
                        menuShouldScrollIntoView={false}
                    />
                }

                <GeneralSelect
                    className='asset-browser-type-filter'
                    inputId='asset-browser-type-filter'
                    options={assetTypeSelectOptions}
                    getOptionLabel={(option) => option.title}
                    getOptionValue={(option) => option.id}
                    value={selectedAssetTypeOption}
                    placeholder={translate('ABB.Powertrain.Frontend.allassettypesLabel')}
                    onChange={(value) => onAssetTypeChange?.(value?.assetType || allAssetTypes)}
                    isSearchable={false}
                    menuShouldScrollIntoView={false}
                />
            </div>

            {isListView && isPowertrainCategory
                ? renderPowertrainAssets()
                : <HierarchyViewContainer
                    selectedAsset={selectedAsset}
                    leftPanelSize={leftPanelSize}
                    allAssets={[]}
                    conditionIndicesView={conditionIndicesView}
                    showFilter={true}
                    onSelectAsset={onSelectAsset}
                    generalFilterLoadingStatuses={[]}
                    activeTab={toggleButtonOptions.ASSETS}
                    selectedOrganization={selectedOrganization}
                    selectedAssetIds={selectedAssetIds}
                    selectedAssetSignalCounts={selectedAssetSignalCounts}
                />}
        </div>
    );
};

AssetBrowserPanelComponent.propTypes = {
    selectedAsset: PropTypes.object,
    selectedOrganization: PropTypes.object,
    conditionIndicesView: PropTypes.string,
    onSelectAsset: PropTypes.func.isRequired,
    searchText: PropTypes.string,
    assetTypeFilter: PropTypes.string,
    assetTypeOptions: PropTypes.array,
    onSearchChange: PropTypes.func,
    onAssetTypeChange: PropTypes.func,
    leftPanelSize: PropTypes.number,
    selectedCategory: PropTypes.string,
    onCategoryChange: PropTypes.func,
    powertrainOptions: PropTypes.array,
    selectedPowertrainId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onPowertrainChange: PropTypes.func,
    powertrainAssets: PropTypes.array,
    isPowertrainLoading: PropTypes.bool,
    selectedAssetIds: PropTypes.array,
    selectedAssetSignalCounts: PropTypes.object
};

export default AssetBrowserPanelComponent;
